import React from "react";

// Time-limited dungeon drops (GET /treasure?treasureType=1 → eventDrops).
export type EventDropFrame = {
  colors?: string[];
};

export type EventDrop = {
  id: number;
  type: number;
  typeId: number;
  level: number;
  quantity: number;
  name: string;
  icon: string;
  chance: number;
  endTime: string;
  frame?: EventDropFrame;
};

// Hex, rgb()/rgba()/hsl()/hsla() or a named colour; anything else is ignored.
const COLOR_PATTERN =
  /^(#[0-9a-f]{3,8}|(rgb|rgba|hsl|hsla)\([0-9.,%\s/-]+\)|[a-z]+)$/i;

/**
 * Parses a server date. The API writes offsets as "+0330", which not every
 * browser's Date parser accepts, so the colon is added first.
 */
export function parseServerDate(value?: string | null): number {
  if (!value) return NaN;
  return Date.parse(value.replace(/([+-]\d{2})(\d{2})$/, "$1:$2"));
}

/** True until the drop's end time; a drop whose end cannot be read stays visible. */
export function isEventDropOpen(drop: EventDrop, now: number = Date.now()) {
  const end = parseServerDate(drop?.endTime);
  return Number.isNaN(end) || end > now;
}

/** The drops still running, so a page left open past the end hides them. */
export function openEventDrops(
  drops: EventDrop[] | undefined | null,
  now: number = Date.now()
): EventDrop[] {
  return Array.isArray(drops)
    ? drops.filter((drop) => isEventDropOpen(drop, now))
    : [];
}

/** The usable frame colours, three of them (the last one repeated), or none. */
export function frameColors(frame?: EventDropFrame): string[] {
  const colors = (frame?.colors || [])
    .filter((color) => typeof color === "string")
    .map((color) => color.trim())
    .filter((color) => COLOR_PATTERN.test(color))
    .slice(0, 3);
  if (colors.length === 0) return [];
  while (colors.length < 3) colors.push(colors[colors.length - 1]);
  return colors;
}

/** The CSS variables that paint the existing badge ring, or undefined for a normal frame. */
export function eventFrameStyle(
  frame?: EventDropFrame
): React.CSSProperties | undefined {
  const colors = frameColors(frame);
  if (colors.length === 0) return undefined;
  return {
    "--frame-1": colors[0],
    "--frame-2": colors[1],
    "--frame-3": colors[2],
  } as React.CSSProperties;
}
